<template>
  <div class="generative-physics-engine dark-viz">
    <PendingViz v-if="isPending" :knowledge-point="knowledgePoint" />
    <template v-else>
      <!-- Animated Mesh Background -->
    <div class="mesh-particles"></div>
    <div class="vignette-overlay"></div>

    <!-- AI Generating HUD (Left Side) -->
    <div class="engine-hud animate-slide-in">
      <div class="parsing-stream glass-card-dark">
        <div class="header">
          <div class="ai-status-dot"></div>
          <i class="el-icon-cpu"></i>
          <span>核心知识解析引擎</span>
        </div>
        <div class="terminal">
          <div v-for="(log, i) in parsingLogs" :key="i" class="log-line">
            <span class="prefix">>></span> {{ log }}
          </div>
          <div class="cursor">_</div>
        </div>
      </div>
      
      <div class="detected-logic-chips">
        <div v-for="tag in detectedLogic" :key="tag" class="logic-tag glow-blue">
          {{ tag }}
        </div>
      </div>

      <!-- Inference Bubble moved here -->
      <div class="inference-hud-section glass-card-dark mt-12" v-if="inferenceText">
        <div class="section-label">生成逻辑推导</div>
        <div class="inference-text">{{ inferenceText }}</div>
      </div>

      <!-- Action Buttons moved here -->
      <div class="runtime-actions-compact mt-12">
        <el-button type="info" class="mini-action" circle size="mini" @click="resetEngine" title="重置">
          <i class="el-icon-refresh-left"></i>
        </el-button>
        <el-button :type="running ? 'warning' : 'success'" class="mini-action" circle size="mini" @click="togglePause" :title="running ? '暂停' : '继续'">
          <i :class="running ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
        </el-button>
      </div>
    </div>

    <!-- Main Rendering Stage -->
    <div class="stage-container" ref="stage">
      <canvas ref="canvas"></canvas>
    </div>
    </template>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import PendingViz from './PendingViz.vue';

export default {
  name: 'SemanticGenerativeEngine',
  components: { PendingViz },
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      activeModel: 'physics', // physics, wave, motion, math
      isPending: false,
      parsingLogs: [],
      detectedLogic: [],
      entities: [],
      envParams: {
        timeScale: { name: '仿真速率', value: 0.5, min: 0.1, max: 2, step: 0.1 }
      },
      inferenceText: '',
      running: true,
      time: 0,
      ctx: null
    }
  },
  watch: {
    knowledgePoint: {
      immediate: true,
      handler(val) {
        if (val) {
          this.processKnowledge(val);
          if (!this.isPending) {
            this.$nextTick(() => {
              this.initCanvas();
            });
          }
        }
      }
    }
  },
  mounted() {
    this.initCanvas();
    this.animate();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.handle);
  },
  methods: {
    initCanvas() {
      const stage = this.$refs.stage;
      if (!stage) return;
      this.width = stage.clientWidth;
      this.height = stage.clientHeight;
      this.ctx = this.$refs.canvas.getContext('2d');
      this.$refs.canvas.width = this.width;
      this.$refs.canvas.height = this.height;
    },
    
    processKnowledge(kp) {
      this.resetData();
      const subject = this.detectSubject(kp);
      this.log(`正在深度解析指令: "${kp}"`);
      
      const text = kp.toLowerCase();
      
      let level = "基础";
      if (this.matchKeywords(text, ['守恒', '感应', '场', '熵', '量子', '多普勒'])) level = "核心/高考级";
      if (this.matchKeywords(text, ['强基', '竞赛', '微分', '积分', '张量', '哈密顿'])) level = "强基/竞赛级";
      
      // 捕获已知暂无精确模型的知识点：复数/共轭等
      if (this.matchKeywords(text, ['复数', '共轭', '虚数'])) {
        this.isPending = true;
        this.log('识别到复数概念体系...');
        this.log('专用复平面模型开发中，暂由通用引擎代理...');
        return;
      }
      
      if (subject === 'history') {
        this.log(`模型匹配: 历史人文知识模块`);
        this.buildHistoryEngine(kp);
        return;
      }
      
      this.log(`模型匹配: 物理科学知识模块 (${level})`);

      // 1. Electromagnetics
      if (this.matchKeywords(text, ['安培', '右手螺旋', '左手定则', '电生磁', '磁感应'])) {
        this.buildAmpereEngine(kp);
      } 
      // 2. Gravitation & Space (Upgraded priority)
      else if (this.matchKeywords(text, ['引力', '天体', '轨道', '行星', '宇宙速度', '卫星', '开普勒'])) {
        this.buildGravitationEngine(kp);
      }
      // 3. Momentum & Energy
      else if (this.matchKeywords(text, ['动量', '冲量', '碰撞', '打击', '反冲'])) {
        this.isPending = true; // 动量板块暂无清晰动画逻辑，提示待开发
        this.log('试图构建动量与冲量系统...');
      } else if (this.matchKeywords(text, ['守恒', '功', '能', '功率', '势能', '动能'])) {
        this.isPending = true; // 能量板块暂用通用模型
        this.log('正在构建功与能转换拓扑...');
      }
      // 3. Kinematics & Circular
      else if (this.matchKeywords(text, ['圆周', '向心', '角速度', '周期', '匀速圆周'])) {
        this.buildCircularEngine(kp);
      } else if (this.matchKeywords(text, ['抛', '自由落体', '加速度', '运动学', '匀变速'])) {
        this.buildProjectileEngine(kp);
      }
      // 4. Forces & Dynamics
      else if (this.matchKeywords(text, ['力', '牛顿', '摩擦', '斜面', '平衡', '压力', '重力'])) {
        this.buildDynamicsEngine(kp);
      }
      // 5. Optics & Waves
      else if (this.matchKeywords(text, ['折射', '反射', '光', '镜', '透镜', '衍射', '干涉'])) {
        this.buildOpticsEngine(kp);
      } else if (this.matchKeywords(text, ['波', '振动', '声', '谐振'])) {
        this.buildWaveEngine(kp);
      }
      // 6. Math & Geometry
      else if (this.matchKeywords(text, ['勾股', '直角', '几何', '三角'])) {
        this.buildPythagoreanEngine(kp);
      } else if (this.matchKeywords(text, ['函数', '坐标', '图表', '解析', '几何'])) {
        this.buildMathEngine(kp);
      }
      // 7. Circuits (New)
      else if (this.matchKeywords(text, ['电路', '电阻', '电流', '电压', '串联', '并联', '欧姆'])) {
        this.buildCircuitEngine(kp);
      }
      // 8. Generic
      else {
        this.isPending = true;
        this.log('正在尝试深度语义匹配...');
        this.log('语义置信度较低，建议开启待开发提示...');
      }
    },

    buildCircuitEngine(kp) {
      this.detectedLogic = ['电学基础', '电路分析'];
      this.inferenceText = `识别至闭合电路逻辑。构建回路模型并应用全电路欧姆定律 I = E / (R + r)。`;
      this.entities.push({ type: 'circuit', r: 10, v: 12, label: kp });
    },

    buildAmpereEngine(kp) {
      this.detectedLogic = ['电磁学', '磁场定向'];
      this.inferenceText = `识别到右手螺旋定则逻辑点。构建无限长直导线磁场模型及 B 向量场。`;
      this.entities.push({ type: 'ampere_wire', label: kp });
    },

    buildMomentumEngine(kp) {
      this.detectedLogic = ['力学', '动量定理'];
      this.inferenceText = `识别到碰撞/冲量特征。模拟质量实体间的动量变换过程验证 p1 + p2 = 常数。`;
      this.entities.push({ type: 'collision', m1: 2, m2: 1, v1: 2, v2: -4, x1: -200, x2: 200, label: kp });
    },

    buildCircularEngine(kp) {
      this.detectedLogic = ['圆周动力学', '矢量追踪'];
      this.inferenceText = `识别到圆周运动。建立极坐标系，追踪向心加速度 a = ω²r 的矢量动态。`;
      this.entities.push({ type: 'circular', r: 150, omega: 1.5, label: kp });
    },

    buildPythagoreanEngine(kp) {
      this.detectedLogic = ['几何结构', '直角约束'];
      this.inferenceText = `识别到勾股空间映射。构建基于 a² + b² = c² 的交互式面积模型。`;
      this.entities.push({ type: 'pythagorean', x: 250, y: 350, a: 120, b: 160, label: kp });
    },

    buildGravitationEngine(kp) {
      this.detectedLogic = ['天体动力学', '开普勒定律'];
      this.inferenceText = `识别至行星轨道参数。应用万有引力模型，展示轨道偏心率及速度场分布。`;
      this.entities.push({ type: 'gravitation', cx: 0, cy: 0, r: 180, label: kp });
    },

    buildOpticsEngine(kp) {
      this.detectedLogic = ['波动光学', '斯涅尔空间'];
      this.inferenceText = `识别到光路折返特征。模拟光线在不同波束阻抗介质间的偏折逻辑。`;
      this.entities.push({ type: 'refraction', label: kp });
    },

    buildProjectileEngine(kp) {
      this.detectedLogic = ['平移运动', '匀变速模型'];
      this.inferenceText = `识别到经典抛体规律。建立分运动独立性模型，验证重力加速度对垂直位移的影响。`;
      this.entities.push({ type: 'projectile_v2', v0: 50, angle: 45, trail: [], label: kp });
    },

    buildEnergyEngine(kp) {
      this.detectedLogic = ['系统总量', '能量迁移'];
      this.inferenceText = `检测到能量变化。实时分析重力势能与动能的无损转化及其在相空间的轨迹。`;
      this.entities.push({ type: 'energy_ball', x: 400, y: 100, r: 200, label: kp });
    },

    buildWaveEngine(kp) {
       this.detectedLogic = ['振动传播', '相速解析'];
       this.inferenceText = `识别到机械波/电磁波特征。展示波面随时间轴的函数漂移：u(x,t)。`;
       this.entities.push({ type: 'wave_view', A: 50, f: 1, label: kp });
    },

    buildDynamicsEngine(kp) {
      this.detectedLogic = ['质点动力学', '受力分解'];
      this.inferenceText = `识别到动力学交互。构建受力物体及其在约束场中的受力矢量平衡。`;
      this.entities.push({ type: 'inclined_plane', angle: 30, x: 100, y: 400, label: kp });
    },

    buildFieldEngine(kp) {
      this.detectedLogic = ['场域空间', '矢量场线'];
      this.inferenceText = `识别至连续场分布。渲染封闭回路或辐射源的感应强度模型。`;
      this.entities.push({ type: 'field_lines', mode: kp.includes('磁') ? 'magnetic' : 'electric', label: kp });
    },

    buildMathEngine(kp) {
       this.detectedLogic = ['数值模拟', '解空间映射'];
       this.inferenceText = `解析抽象数学逻辑。构建基于解析式的数值图形投影。`;
       this.entities.push({ type: 'math_func', label: kp });
    },

    buildGenericLogicalEngine(kp) {
      this.detectedLogic = ['语义关联', '知识拓扑'];
      this.inferenceText = `引擎未能匹配特定物理模型。启用通用语义拓扑引擎进行核心概念关联展示。`;
      this.entities.push({ type: 'node', label: kp });
      const related = ['物理模型', '核心公式', '典型案例', '实验方法']; 
      related.forEach((r, i) => {
        this.entities.push({ type: 'sub_node', label: r });
      });
    },

    buildHistoryEngine(kp) {
      this.detectedLogic = ['史料分析', '时空关联'];
      this.inferenceText = `识别至历史人文课题。构建史论结合模型，解析核心背景与关键历史影响。`;
      this.entities.push({ type: 'node', label: kp });
      const related = ['史料解析', '核心背景', '关键人物', '因果关联']; 
      related.forEach((r, i) => {
        this.entities.push({ type: 'sub_node', label: r });
      });
    },

    detectSubject(kp) {
      const text = kp.toLowerCase();
      // 历史关键词命中
      if (this.matchKeywords(text, ['历史', '史', '秦', '汉', '唐', '宋', '元', '明', '清', '近代', '现代', '战争', '封建', '民主', '政治', '文明', '材料分析'])) return 'history';
      return 'science';
    },

    updatePhysics(ts) {
      const dt = 0.016 * ts;
      this.entities.forEach(ent => {
        if (ent.type === 'projectile_v2') {
          const v0 = ent.v0;
          const rad = (ent.angle * Math.PI) / 180;
          const vx = v0 * Math.cos(rad);
          const vy = v0 * Math.sin(rad) - 9.8 * this.time;
          ent.px = vx * this.time;
          ent.py = vy * this.time - 0.5 * 9.8 * this.time * this.time;
          if (this.time % 0.1 < 0.02) ent.trail.push({ x: ent.px, y: ent.py });
          if (ent.trail.length > 50) ent.trail.shift();
        } else if (ent.type === 'energy_ball') {
          const gravity = 9.8;
          const length = ent.r;
          ent.angle = 0.5 * Math.cos(Math.sqrt(gravity / length) * this.time * 20);
          ent.px = length * Math.sin(ent.angle);
          ent.py = length * Math.cos(ent.angle);
        } else if (ent.type === 'collision') {
          ent.x1 += ent.v1 * dt * 5;
          ent.x2 += ent.v2 * dt * 5;
          if (Math.abs(ent.x1 - ent.x2) < 40) {
             const v1 = ((ent.m1 - ent.m2) * ent.v1 + 2 * ent.m2 * ent.v2) / (ent.m1 + ent.m2);
             const v2 = ((ent.m2 - ent.m1) * ent.v2 + 2 * ent.m1 * ent.v1) / (ent.m1 + ent.m2);
             ent.v1 = v1; ent.v2 = v2;
          }
        } else if (ent.type === 'circular') {
          ent.angle = (this.time * ent.omega) % (Math.PI * 2);
        } else if (ent.type === 'gravitation') {
          ent.angle = (this.time * 2) % (Math.PI * 2);
          ent.px = ent.cx + Math.cos(ent.angle) * ent.r;
          ent.py = ent.cy + Math.sin(ent.angle) * ent.r;
        }
      });
    },

    draw() {
      const ctx = this.ctx;
      if (!ctx) return;
      ctx.clearRect(0, 0, this.width, this.height);
      
      const centerX = this.width / 2;
      const centerY = this.height / 2;

      this.entities.forEach(ent => {
        ctx.save();
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#00d2ff';
        ctx.strokeStyle = '#0002ff';
        ctx.fillStyle = '#00d2ff';
        ctx.lineWidth = 2;

        if (ent.type === 'collision') {
           ctx.translate(centerX, centerY);
           // Labelling
           ctx.fillStyle = '#fff'; ctx.fillText('m1v1 + m2v2 = Const', -50, -100);
           // Balls
           ctx.beginPath(); ctx.arc(ent.x1, 0, 20, 0, Math.PI*2); ctx.stroke();
           ctx.beginPath(); ctx.arc(ent.x2, 0, 20, 0, Math.PI*2); ctx.stroke();
           // Velocity arrows
           ctx.beginPath(); ctx.moveTo(ent.x1, 0); ctx.lineTo(ent.x1 + ent.v1*20, 0); ctx.stroke();
           ctx.beginPath(); ctx.moveTo(ent.x2, 0); ctx.lineTo(ent.x2 + ent.v2*20, 0); ctx.stroke();
        } else if (ent.type === 'circular') {
           ctx.translate(centerX, centerY);
           ctx.beginPath(); ctx.setLineDash([5,5]); ctx.arc(0, 0, ent.r, 0, Math.PI*2); ctx.stroke(); ctx.setLineDash([]);
           const px = Math.cos(ent.angle) * ent.r;
           const py = Math.sin(ent.angle) * ent.r;
           ctx.beginPath(); ctx.arc(px, py, 15, 0, Math.PI*2); ctx.fill();
           // Centripetal force vector
           ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(0,0); ctx.stroke();
           ctx.fillStyle='#fff'; ctx.fillText('Fn = mv²/r', px+20, py);
        } else if (ent.type === 'ampere_wire') {
           // Wire
           ctx.lineWidth = 4;
           ctx.beginPath(); ctx.moveTo(centerX, centerY - 200); ctx.lineTo(centerX, centerY + 200); ctx.stroke();
           ctx.lineWidth = 2;
           // Current Arrow
           ctx.beginPath(); ctx.moveTo(centerX, centerY - 100); ctx.lineTo(centerX - 10, centerY - 80); ctx.moveTo(centerX, centerY - 100); ctx.lineTo(centerX+10, centerY - 80); ctx.stroke();
           ctx.fillStyle = '#fff'; ctx.fillText('电流 I', centerX + 15, centerY - 100);
           // Magnetic Circles (Perspective)
           for(let i=1; i<=4; i++) {
              ctx.beginPath(); ctx.ellipse(centerX, centerY, 40 * i, 15 * i, 0, 0, Math.PI * 2); ctx.stroke();
              // Arrow on ellipse
              const ex = centerX + 40 * i; const ey = centerY;
              ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(ex - 5, ey - 5); ctx.moveTo(ex, ey); ctx.lineTo(ex + 5, ey - 5); ctx.stroke();
           }
           ctx.fillText('磁感线 B', centerX + 100, centerY + 50);
        } else if (ent.type === 'gravitation') {
           // Center Planet
           ctx.beginPath(); ctx.arc(centerX, centerY, 40, 0, Math.PI*2);
           ctx.fillStyle = 'rgba(0, 210, 255, 0.2)'; ctx.fill();
           ctx.strokeStyle = '#00d2ff'; ctx.stroke();
           // Orbit
           ctx.beginPath(); ctx.setLineDash([5,5]); ctx.arc(centerX, centerY, ent.r, 0, Math.PI*2); ctx.stroke();
           ctx.setLineDash([]);
           // Planet labels
           ctx.fillStyle = '#fff'; ctx.fillText('M', centerX-5, centerY+5);
           // Satellite
           const sx = centerX + Math.cos(ent.angle) * ent.r;
           const sy = centerY + Math.sin(ent.angle) * ent.r;
           ctx.beginPath(); ctx.arc(sx, sy, 10, 0, Math.PI*2);
           ctx.fillStyle = '#fff'; ctx.fill();
           // Gravity vector
           ctx.beginPath(); ctx.setLineDash([2,2]); ctx.moveTo(sx, sy); ctx.lineTo(centerX, centerY); ctx.stroke();
           ctx.setLineDash([]);
           ctx.fillText('F_g = GMm/r²', sx+15, sy);
        } else if (ent.type === 'refraction') {
           ctx.translate(centerX - 300, centerY - 200);
           // Normal line
           ctx.beginPath(); ctx.setLineDash([5,5]); ctx.moveTo(300, 0); ctx.lineTo(300, 400); ctx.stroke();
           ctx.setLineDash([]);
           // Boundary
           ctx.beginPath(); ctx.moveTo(0, 200); ctx.lineTo(600, 200); ctx.lineWidth = 1; ctx.stroke();
           ctx.fillStyle = 'rgba(0, 210, 255, 0.05)'; ctx.fillRect(0, 200, 600, 200);
           // Incoming light
           ctx.beginPath(); ctx.moveTo(150, 50); ctx.lineTo(300, 200); ctx.strokeStyle = '#00d2ff'; ctx.stroke();
           // Refracted light
           ctx.beginPath(); ctx.moveTo(300, 200); ctx.lineTo(400, 350); ctx.stroke();
           ctx.fillStyle = '#fff'; ctx.fillText('n1=1.0 (空气)', 310, 180); ctx.fillText('n2=1.5 (玻璃)', 310, 230);
        } else if (ent.type === 'energy_ball') {
           ctx.translate(centerX, centerY - 150);
           ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(ent.px, ent.py); ctx.stroke();
           ctx.beginPath(); ctx.arc(ent.px, ent.py, 20, 0, Math.PI*2); ctx.fill();
           ctx.translate(-200, 300);
           const ek = Math.abs(Math.sin(ent.angle)) * 100;
           const ep = 100 - ek;
           ctx.fillStyle = '#00d2ff'; ctx.fillRect(0, -ek, 20, ek); ctx.fillText('Ek', 0, 15);
           ctx.fillStyle = '#3a7bd5'; ctx.fillRect(40, -ep, 20, ep); ctx.fillText('Ep', 40, 15);
        } else if (ent.type === 'pythagorean') {
          ctx.translate(centerX - ent.b / 2 - 50, centerY + ent.a / 2);
          ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(ent.b, 0); ctx.lineTo(ent.b, -ent.a); ctx.closePath(); ctx.stroke();
          ctx.setLineDash([5, 5]); ctx.strokeRect(0, 0, ent.b, ent.b); ctx.strokeRect(ent.b, -ent.a, ent.a, ent.a); ctx.setLineDash([]);
          ctx.fillStyle = '#fff'; ctx.fillText('a', ent.b + 5, -ent.a / 2); ctx.fillText('b', ent.b / 2, 15); ctx.fillText('c', ent.b / 2 - 20, -ent.a / 2); ctx.fillText('a² + b² = c²', ent.b + 20, 50);
        } else if (ent.type === 'projectile_v2') {
          ctx.translate(centerX - 250, centerY + 150);
          ctx.beginPath(); ent.trail.forEach(p => ctx.lineTo(p.x, -p.y)); ctx.stroke();
          ctx.beginPath(); ctx.arc(ent.px, -ent.py, 8, 0, Math.PI * 2); ctx.fill();
        } else if (ent.type === 'wave_view') {
           ctx.beginPath();
           for(let x=0; x<600; x++) {
              let y = ent.A * Math.sin(x * 0.05 + this.time * ent.f * 5);
              ctx.lineTo(centerX - 300 + x, centerY + y);
           }
           ctx.stroke();
        } else if (ent.type === 'node') {
          ctx.translate(centerX, centerY);
          const pulse = 1 + Math.sin(this.time * 2) * 0.05;
          ctx.beginPath(); 
          ctx.arc(0, 0, 45 * pulse, 0, Math.PI * 2); 
          ctx.stroke();
          
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 14px Inter';
          ctx.textAlign = 'center';
          ctx.fillText(ent.label, 0, 5);

          const subNodes = this.entities.filter(e => e.type === 'sub_node');
          subNodes.forEach((sub, i) => { 
             const angle = (i / subNodes.length) * Math.PI * 2 + this.time * 0.2;
             const dist = 160 + Math.sin(this.time + i) * 10;
             const sx = Math.cos(angle) * dist;
             const sy = Math.sin(angle) * dist;
             
             // Dynamic flow line
             ctx.beginPath(); 
             ctx.setLineDash([5, 15]);
             ctx.lineDashOffset = -this.time * 20;
             ctx.moveTo(Math.cos(angle) * 45, Math.sin(angle) * 45); 
             ctx.lineTo(sx - Math.cos(angle) * 35, sy - Math.sin(angle) * 35); 
             ctx.stroke(); 
             ctx.setLineDash([]);
          });
        } else if (ent.type === 'sub_node') {
          const subNodes = this.entities.filter(e => e.type === 'sub_node');
          const i = subNodes.indexOf(ent);
          const angle = (i / subNodes.length) * Math.PI * 2 + this.time * 0.2;
          const dist = 160 + Math.sin(this.time + i) * 10;
          const sx = centerX + Math.cos(angle) * dist;
          const sy = centerY + Math.sin(angle) * dist;
          
          const pulse = 1 + Math.sin(this.time * 3 + i) * 0.08;
          ctx.beginPath(); 
          ctx.arc(sx, sy, 35 * pulse, 0, Math.PI * 2); 
          ctx.stroke(); 
          
          ctx.fillStyle = 'rgba(0, 210, 255, 0.1)';
          ctx.fill();
          
          ctx.fillStyle = '#fff'; 
          ctx.font = '12px Inter';
          ctx.textAlign = 'center'; 
          ctx.fillText(ent.label, sx, sy + 5);
        } else if (ent.type === 'math_func') {
           ctx.beginPath();
           ctx.translate(centerX, centerY);
           for(let x=-200; x<200; x+=2) {
              const y = 80 * Math.sin(x * 0.02 + this.time * 2) * Math.cos(this.time);
              ctx.lineTo(x, y);
           }
           ctx.strokeStyle = '#00d2ff';
           ctx.stroke();
           ctx.fillStyle = '#fff'; ctx.fillText('y = f(x, t) 解析映射', -50, 120);
        } else if (ent.type === 'inclined_plane') {
           const floorY = centerY + 100;
           const angle = ent.angle * Math.PI / 180;
           const len = 400;
           const x1 = centerX - 200;
           const y1 = floorY;
           const x2 = x1 + len * Math.cos(angle);
           const y2 = floorY - len * Math.sin(angle);
           // Inclined plane
           ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.lineTo(x2, y1); ctx.closePath();
           ctx.fillStyle = 'rgba(0, 210, 255, 0.1)'; ctx.fill();
           ctx.stroke();
           // Object on plane
           const objX = x1 + (len/2) * Math.cos(angle);
           const objY = floorY - (len/2) * Math.sin(angle);
           ctx.save();
           ctx.translate(objX, objY);
           ctx.rotate(-angle);
           ctx.strokeRect(-20, -40, 40, 40);
           ctx.fillStyle = '#fff'; ctx.fillText('m', -5, -15);
           // Vectors
           ctx.beginPath(); ctx.moveTo(0, -20); ctx.lineTo(0, 50); ctx.setLineDash([2,2]); ctx.stroke(); // Gravity
           ctx.rotate(angle); ctx.restore();
           ctx.fillStyle = '#fff'; ctx.fillText('F_g = mg', objX - 10, objY + 60);
        } else if (ent.type === 'field_lines') {
           for(let i=0; i<12; i++) { ctx.beginPath(); ctx.ellipse(centerX, centerY, 50 + i*30, 30 + i*15, 0, 0, Math.PI*2); ctx.stroke(); }
        }
        ctx.restore();
      });
    },

    matchKeywords(text, keys) {
      return keys.some(k => text.includes(k));
    },

    log(msg) {
      this.parsingLogs.push(msg);
      if (this.parsingLogs.length > 5) this.parsingLogs.shift();
    },

    resetData() {
      this.isPending = false;
      this.parsingLogs = [];
      this.entities = [];
      this.envParams = {
        timeScale: { name: '仿真速率', value: 0.5, min: 0.1, max: 2, step: 0.1 }
      };
      this.detectedLogic = [];
      this.time = 0;
      this.ctx = null;
    },

    animate() {
      if (this.running) {
        const ts = this.envParams.timeScale.value;
        this.time += 0.01 * ts;
        this.updatePhysics(ts);
      }
      this.draw();
      this.handle = requestAnimationFrame(this.animate);
    },

    handleEnvChange() {},
    resetEngine() { this.processKnowledge(this.knowledgePoint); },
    togglePause() { this.running = !this.running; }
  }
}
</script>

<style scoped lang="scss">
.dark-viz {
  background: radial-gradient(circle at 70% 30%, #0a1128 0%, #02040a 100%) !important;
  color: #fff !important;
  border-radius: 20px;
  box-shadow: inset 0 0 100px rgba(0, 0, 0, 0.8);
}

.mesh-particles {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.05) 1px, transparent 0);
  background-size: 40px 40px;
  pointer-events: none;
}

.vignette-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, transparent 30%, rgba(0, 0, 0, 0.4) 100%);
  pointer-events: none;
}

.generative-physics-engine {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', -apple-system, sans-serif;
  height: 100%;
}

.engine-hud {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;
  pointer-events: none;

  .parsing-stream {
    width: 280px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(0, 210, 255, 0.3);
    background: rgba(13, 17, 23, 0.9) !important;
    backdrop-filter: blur(12px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);

    .header {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.85rem;
      color: #00d2ff !important;
      font-weight: 700;
      margin-bottom: 12px;
      letter-spacing: 1px;

      .ai-status-dot {
        width: 8px; height: 8px;
        background: #00d2ff;
        border-radius: 50%;
        box-shadow: 0 0 10px #00d2ff;
        animation: pulse 2s infinite;
      }
    }

    .terminal {
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
      font-size: 0.7rem;
      color: rgba(0, 210, 255, 0.9) !important;
      line-height: 1.6;

      .log-line {
        margin-bottom: 4px;
        animation: fadeInLine 0.3s ease-out;
      }

      .prefix { color: #00d2ff; opacity: 0.8; }
      .cursor { display: inline-block; animation: blink 1s step-end infinite; }
    }
  }

  .detected-logic-chips {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    .logic-tag {
      padding: 4px 12px;
      background: rgba(0, 210, 255, 0.2) !important;
      border: 1px solid rgba(0, 210, 255, 0.5) !important;
      color: #00d2ff !important;
      font-size: 0.7rem;
      border-radius: 20px;
      backdrop-filter: blur(4px);
      text-shadow: 0 0 10px rgba(0, 210, 255, 1);
    }
  }

  .inference-hud-section {
    width: 280px;
    padding: 16px;
    border-radius: 12px;
    border: 1.5px solid #00d2ff !important;
    background: rgba(13, 17, 23, 0.95) !important;
    backdrop-filter: blur(16px);
    box-shadow: 0 0 20px rgba(0, 210, 255, 0.2);

    .section-label { 
      font-size: 0.7rem; 
      color: #00d2ff !important; 
      text-transform: uppercase; 
      margin-bottom: 8px; 
      letter-spacing: 1px;
      font-weight: bold;
    }
    .inference-text { 
      font-size: 0.85rem; 
      line-height: 1.6; 
      color: #ffffff !important;
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
      font-weight: 500;
    }
  }

  .runtime-actions-compact {
    display: flex;
    gap: 10px;
    .mini-action {
      background: rgba(255, 255, 255, 0.1) !important;
      border: 1px solid rgba(0, 210, 255, 0.3) !important;
      color: #fff !important;
      &:hover { background: rgba(0, 210, 255, 0.4) !important; border-color: #00d2ff !important; transform: scale(1.1); }
    }
  }
}

.stage-container {
  flex: 1;
  position: relative;
  background: transparent !important; // 强制透明以显示 parent 的渐变背景
  border: none !important;
  box-shadow: none !important;
  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.v-label {
  position: absolute;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  min-width: 80px;

  .entity-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 4px;
  }

  .data-tag-group {
    display: flex;
    gap: 8px;
    .data-badge {
      font-size: 0.65rem;
      color: rgba(255, 255, 255, 0.6);
      font-family: monospace;
      .lbl { color: #00d2ff; font-weight: bold; }
    }
  }
}

.mt-12 { margin-top: 12px; }

// Animations
@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.5; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes blink {
  50% { opacity: 0; }
}

@keyframes fadeInLine {
  from { opacity: 0; transform: translateX(-5px); }
  to { opacity: 1; transform: translateX(0); }
}

.animate-slide-in { animation: slideIn 0.8s cubic-bezier(0.23, 1, 0.32, 1); }
.animate-pop-in { animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275); }

@keyframes slideIn { from { opacity: 0; transform: translateX(-30px); } }
@keyframes popIn { from { opacity: 0; transform: scale(0.8); } }

</style>
